import { useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import navigation from "@/docs-content/navigation.json";
import { trackGA4Event } from "@/lib/analytics";
import clsx from "clsx";

interface NavItem { title: string; href: string; badge?: string; }
interface NavSection { title: string; items: NavItem[]; }

function sectionForPath(sections: NavSection[], pathname: string): string | null {
  for (const section of sections) {
    if (section.items.some((item) => item.href === pathname)) return section.title;
  }
  return null;
}

const sections = navigation.sections as NavSection[];

function Chevron() {
  return (
    <svg className="dchev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

export default function DocsSidebar() {
  const { pathname } = useLocation();

  // Collapse all but the active section (falls back to the first section).
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const active = sectionForPath(sections, pathname);
    return new Set<string>([active ?? sections[0]?.title ?? ""]);
  });

  const [prevPathname, setPrevPathname] = useState(pathname);
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    const active = sectionForPath(sections, pathname);
    if (active && !expanded.has(active)) setExpanded(new Set(expanded).add(active));
  }

  const toggle = useCallback((title: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  }, []);

  return (
    <nav className="docs-nav">
      <div className="docs-cta">
        <a
          className="docs-cta-key"
          href="/dashboard"
          onClick={() => trackGA4Event("tool_cta_click", { cta: "get_api_key", location: "docs" })}
        >Get a free API key →</a>
        <span className="docs-cta-note">10,000 requests/mo · no credit card</span>
        <a
          className="docs-cta-mcp"
          href="/docs/mcp/overview"
          onClick={() => trackGA4Event("tool_cta_click", { cta: "add_mcp", location: "docs" })}
        >Building an AI agent? Add via MCP →</a>
      </div>
      {sections.map((section) => {
        const open = expanded.has(section.title);
        const has = section.items.some((i) => i.href === pathname);
        return (
          <div className="dgrp" key={section.title}>
            <button
              className={clsx("dgrphead", open && "open", has && "has")}
              onClick={() => toggle(section.title)}
              aria-expanded={open}
            >
              <Chevron />
              <span className="dlbl">{section.title}</span>
            </button>
            <div className={clsx("ditems", open ? "open" : "shut")}>
              <div>
                {section.items.map((item) => {
                  const active = pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={clsx("dit", active && "active")}
                      aria-current={active ? "page" : undefined}
                    >
                      <span>{item.title}</span>
                      {item.badge && <span className="dbadge">{item.badge}</span>}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </nav>
  );
}
